import { Component, ElementRef, Inject, Input, OnInit } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { ConnectSlideComponent } from '@app/blocks/ui/slide/slide.component';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { SignupService } from '../services/signup.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DataService } from '@app/core/services/data.service';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import { formatFormErrors } from '@app/shared/formly/form-config';
import { SlideCommonService } from '@app/application/common/slide-common.service';
import { WINDOW, WINDOW_PROVIDERS } from '@app/shared/services/window/window.service';
import { SignupDataService } from '../services/signup-data.service';
import * as _ from 'lodash';
import { regEx } from '@app/core/constants/regEx';

@Component({
    selector: 'app-emergency-contacts',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ConnectSlideComponent,
        SpinnerComponent,
        NgIf
    ],
    providers: [
        WINDOW_PROVIDERS
    ],
    templateUrl: './emergency-contacts.component.html',
    styleUrl: './emergency-contacts.component.scss'
})
export class EmergencyContactsComponent implements OnInit {
    @Input() slideNumber: string | undefined;
    @Input() finalSlide: boolean = false;
    currentSlide = 'emergencyContacts';
    errors: any = [];
    errorMessage: string = "";
    showFormErrors = false; //error summary
    loading = true; // spinner
    showSubmit = false;
    submitting = false;
    isDisabled = false;
    active = this.signupService.getActive();

    private destroy$ = new Subject<void>();

    model: any = this.signupService.getEmergencyContactsModel();

    emergencyContactsDataLoaded = false;

    emergencyContactsForm: FormGroup = this.fb.group({
        hasEmergencyContacts: [ false, Validators.required ],
        contacts: this.fb.array([])
    }, { validators: [ this.requiredIfEmergencyContacts ] });

    requiredIfEmergencyContacts(control: AbstractControl) {
        if (control.value.hasEmergencyContacts === false) {
            return null;
        }
        else {
            if (control.value.contacts.length === 0) {
                return { requiredIfEmergencyContacts: true }
            }
            else {
                return null;
            }
        }
    }

    subscription = this.signupService.subscribeEmergencyContactsModel((data: any) => {
        this.model = data;
        // this.model.applicationForm = 'ops'; //
        // this.model.isJointApplication = true; //

        this.initFormControls(this.model);

        this.formState.applicationForm = this.signupService.getAdditionalDetailsModel().applicationForm;
        this.signupService.setFormState(this.formState);

        this.emergencyContactsDataLoaded = !_.isEmpty(this.model);

        this.loading = false;
        this.showSubmit = true;
    });

    constructor(
        private signupService: SignupService,
        private signupDataService: SignupDataService,
        private dataService: DataService,
        private logger: LoggerService,
        private slideCommon: SlideCommonService,
        private el: ElementRef,
        @Inject(WINDOW) private window: Window,
        private fb: FormBuilder
    ) { }

    get formState() {
        return this.signupService.formState;
    }

    initFormControls(model: any) {
        this.emergencyContactsForm.patchValue({
            hasEmergencyContacts: model.hasEmergencyContacts,
        });

        //reset state to prevent duplication
        this.contacts.setValue([]);

        if (model.contacts && model.contacts.length > 0) {
            model.contacts.forEach((contact: any) => {
                this.contacts.push(this.createEmergencyContact(contact));
            })
        }
        else if (model.contacts.length === 0) { // add empty contact for initial render
            this.contacts.push(this.createEmergencyContact());
        }
    }

    onSubmit() {
        if (this.emergencyContactsForm.value.hasEmergencyContacts === false) {
            this.contacts.clear();
        }

        this.emergencyContactsForm.markAllAsTouched();

        if (this.emergencyContactsForm.valid) {
            console.log("VALID");

            console.log(this.model);

            const { hasEmergencyContacts, contacts } = this.emergencyContactsForm.value;

            this.model.hasEmergencyContacts = hasEmergencyContacts;
            this.model.contacts = contacts;

            this.errors = [];
            this.showFormErrors = false;

            this.isDisabled = true;
            this.submitting = true;

            //firstValueFrom will throw an error if the post response is 200 but null
            this.signupDataService.postEmergencyContacts(this.model).pipe(takeUntil(this.destroy$)).subscribe({
                next: () => {
                    this.logger.success('emergency contacts post success');

                    this.formState.progress.emergencyContactsComplete = true;
                    this.signupService.setFormState(this.formState);

                    this.signupService.goForward('emergencyContacts');

                    this.submitting = false;
                    this.isDisabled = false;

                    this.errorMessage = '';

                    this.slideCommon.scroll(this.active.active);
                },
                error: (error: any) => {
                    this.isDisabled = false;
                    this.submitting = false;
                    this.logger.error('post emergencyContacts error', error);

                    this.errorMessage = 'Error saving your emergency contact details, please contact support.';
                }
            })
        }
        else {
            console.log("INVALID", this.emergencyContactsForm.errors)
            const renderTime = 500;
            const revealTime = renderTime + 250;

            setTimeout(() => {
                this.errors = formatFormErrors(this.el, this.emergencyContactsForm);
                this.showFormErrors = true;
            }, renderTime);

            setTimeout(() => {
                this.scrollToId('emergency-contacts-error-summary');
            }, revealTime);
        }
    }

    createEmergencyContact(item?: any) {
        return this.fb.group({
            firstName: [ item?.firstName, [ Validators.required, Validators.maxLength(100) ] ],
            lastName: [ item?.lastName, [ Validators.required, Validators.maxLength(100) ] ],
            phone: [ item?.phone, [ Validators.required, Validators.maxLength(15), Validators.pattern(regEx.phonePattern) ] ],
            email: [ item?.email, [ Validators.required, Validators.email, Validators.pattern(regEx.emailPattern), Validators.maxLength(100) ] ],
            isLeadFinancialPowerOfAttorney: [ item?.isLeadFinancialPowerOfAttorney ],
            isLeadMedicalPowerOfAttorney: [ item?.isLeadMedicalPowerOfAttorney ],
            isLeadNextOfKin: [ item?.isLeadNextOfKin ],
            isJointFinancialPowerOfAttorney: [ item?.isJointFinancialPowerOfAttorney ],
            isJointMedicalPowerOfAttorney: [ item?.isJointMedicalPowerOfAttorney ],
            isJointNextOfKin: [ item?.isJointNextOfKin ],
            isPetContact: [ item?.isPetContact, Validators.required ]
        });
    }

    onBack() {
        this.loading = false;
        this.isDisabled = false;
        this.signupService.goBack('emergencyContacts');
        this.slideCommon.scroll(this.active.active);
    }

    scrollToId(id: string) {
        this.slideCommon.scrollToId(id, this.el, this.window);
    }

    ngOnInit() {
        console.log('emergencyContacts onInit');
        this.signupService.getActive();

        const emergencyContactsControl = this.emergencyContactsForm.get('hasEmergencyContacts');

        emergencyContactsControl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
            if (val === true && this.emergencyContactsForm.value.contacts.length === 0 && this.emergencyContactsForm.touched) {
                this.contacts.push(this.createEmergencyContact())
            }
            // if (oldVal === true && newVal === false) {
            //     console.log('pruning');
            //     var index = 0
            //     while (index < vm.model.contacts.length) {
            //         var el = vm.model.contacts[ index ];
            //         console.log('keys', Object.keys(el));
            //         if (Object.keys(el).length <= 1) {
            //             vm.model.contacts.splice(index, 1)
            //         } else {
            //             index++;
            //         }

            //     }
            //     console.log('pruned', vm.model.contacts);
            // };
        })
    }

    add() {
        this.contacts.push(this.createEmergencyContact())
    }

    remove(i: number) {
        this.contacts.removeAt(i);
    }

    // helpers
    get contacts() {
        return this.emergencyContactsForm.get('contacts') as FormArray;
    }

}
